import React from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { FileEdit as Edit } from 'lucide-react';

interface ContractHistoryTableProps {
  contracts: Array<{
    id: string;
    type: 'cold_calling' | 'mailing' | 'growth';
    billing_unit: 'days' | 'campaign' | 'package';
    start_date: string;
    end_date: string | null;
    days_per_month: number | null;
    monthly_price: number | null;
    price_per_day: number | null;
    version: number;
  }>;
  onEdit: (contract: any) => void;
}

const ContractHistoryTable: React.FC<ContractHistoryTableProps> = ({ contracts, onEdit }) => {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Non définie';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: fr });
    } catch {
      return 'Non définie';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'cold_calling':
        return '📞 Cold Calling';
      case 'mailing':
        return '📧 Mailing';
      case 'growth':
        return '🚀 Growth';
      default:
        return type;
    }
  };

  const getPriceDisplay = (contract: typeof contracts[0]) => {
    if (!contract.monthly_price) return '-';

    if (contract.type === 'cold_calling') {
      return `${contract.monthly_price.toLocaleString('fr-FR')} €/mois`;
    } else if (contract.type === 'mailing') {
      return `${contract.monthly_price.toLocaleString('fr-FR')} €/mois`;
    } else if (contract.type === 'growth') {
      return `${contract.monthly_price.toLocaleString('fr-FR')} €`;
    }
    return `${contract.monthly_price.toLocaleString('fr-FR')} €`;
  };

  if (contracts.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-8 text-center border border-gray-200">
        <p className="text-gray-500">Aucun contrat précédent</p>
      </div>
    );
  }

  const groupedContracts = contracts.reduce((acc, contract) => {
    if (!acc[contract.type]) {
      acc[contract.type] = [];
    }
    acc[contract.type].push(contract);
    return acc;
  }, {} as Record<string, typeof contracts>);

  return (
    <div className="space-y-6">
      {Object.entries(groupedContracts).map(([type, typeContracts]) => (
        <div key={type} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
            <h4 className="text-sm font-semibold text-gray-900">
              {getTypeLabel(type)}
            </h4>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Période
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prix
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {typeContracts.map((contract) => (
                <tr key={contract.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    V{contract.version}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(contract.start_date)} → {formatDate(contract.end_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#E91E8C]">
                    {getPriceDisplay(contract)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Clôturé
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => onEdit(contract)}
                      className="inline-flex items-center gap-1 px-3 py-1 text-sm text-[#EE3195] hover:bg-pink-50 rounded-lg transition-colors"
                    >
                      <Edit className="h-4 w-4" />
                      Modifier
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

export default ContractHistoryTable;
